/**
 * Fractional ranking for sibling order.
 *
 * Dragging a task to a new position must write exactly ONE row. If order were
 * an integer column we would have to renumber every following sibling on each
 * drag — O(n) writes, and a broadcast storm to every connected client. Instead
 * each task holds a short string key, and reordering just mints a new key that
 * sorts strictly between its two new neighbours.
 *
 * This is the canonical fractional-index midpoint algorithm over base 62.
 * The alphabet is in ASCII order (digits < uppercase < lowercase) so SQLite's
 * default BINARY collation on `tasks.rank` gives exactly the order we mean.
 *
 * Invariant: a key never ends in the lowest digit ("0"). The algorithm relies
 * on it — a trailing "0" would leave no room to insert below the key without
 * lengthening it unboundedly, so we assert rather than silently corrupt order.
 */

const ALPHABET = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
const BASE = ALPHABET.length;

/** The key handed to the very first item in an empty sibling list. */
export const FIRST_RANK = midpoint("", null);

function digit(ch: string): number {
  const i = ALPHABET.indexOf(ch);
  if (i < 0) throw new Error(`rank: character ${JSON.stringify(ch)} is not in the alphabet`);
  return i;
}

/**
 * Smallest-effort key strictly between `a` and `b`.
 * `a` is "" for "before everything"; `b` is null for "after everything".
 */
function midpoint(a: string, b: string | null): string {
  if (b !== null && a >= b) throw new Error(`rank: bounds out of order (${a} >= ${b})`);
  if (a.endsWith("0") || (b !== null && b.endsWith("0"))) {
    throw new Error(`rank: bound ends in a trailing zero (${a}, ${b})`);
  }

  if (b !== null) {
    // Copy the shared prefix through untouched and recurse on the first
    // position where the two bounds actually diverge.
    let n = 0;
    while ((a[n] ?? "0") === b[n]) n++;
    if (n > 0) return b.slice(0, n) + midpoint(a.slice(n), b.slice(n));
  }

  const da = a === "" ? 0 : digit(a[0]!);
  const db = b === null ? BASE : digit(b[0]!);

  if (db - da > 1) {
    // There is at least one unused digit between them: take the middle one.
    return ALPHABET[Math.round(0.5 * (da + db))]!;
  }
  if (b !== null && b.length > 1) {
    // The digits are adjacent but `b` continues, so `b`'s first digit alone
    // already sits below `b` and above `a`.
    return b.slice(0, 1);
  }
  // Digits are adjacent and there is no room at this position: keep `a`'s digit
  // and find room one position deeper.
  return ALPHABET[da]! + midpoint(a.slice(1), null);
}

/**
 * Public entry point. Pass the rank of the sibling above (`null` when dropping
 * at the top) and the sibling below (`null` when dropping at the bottom).
 */
export function rankBetween(above: string | null, below: string | null): string {
  return midpoint(above ?? "", below);
}
